<script setup lang="ts">
import { SfIconChevronLeft, SfIconChevronRight } from '@storefront-ui/vue';
import { sortQueryParams } from '~/lib/utils';

const props = defineProps<{
  currentPage: number
  totalItems: number
  pageSize: number
}>();

const router = useRouter();
const route = useRoute();

const pageCount = computed(() => Math.max(1, Math.ceil(props.totalItems / props.pageSize)));
const currentPage = computed(() => Math.min(Math.max(props.currentPage, 1), pageCount.value));
const isFirstPage = computed(() => currentPage.value === 1);
const isLastPage = computed(() => currentPage.value === pageCount.value);

const goToPage = async (page: number) => {
  const nextPage = Math.min(Math.max(page, 1), pageCount.value);

  if (nextPage === currentPage.value) return;

  const query = { ...route.query };
  if (nextPage === 1) {
    delete query.page;
  } else {
    query.page = String(nextPage);
  }

  await router.push({ query: sortQueryParams(query) });

  if (import.meta.client) {
    window.scrollTo({ top: 10, behavior: 'smooth' });
  }
};

const pages = computed(() => {
  return Array.from(Array(pageCount.value))
      .map((_, i) => i + 1)
      .slice(Math.max(0, currentPage.value - 2), Math.min(currentPage.value + 2, pageCount.value));
});
</script>

<template>
  <nav
    class="flex justify-between items-center md:justify-start pt-3"
    role="navigation"
    aria-label="pagination"
    data-testid="pagination"
  >
    <button
      type="button"
      class="px-4 py-3 btn flex whitespace-nowrap rounded-md text-neutral-500 hover:bg-primary-100 hover:text-primary-800 active:bg-primary-200 active:text-primary-900"
      @click="goToPage(currentPage - 1)"
      :disabled="isFirstPage"
      :aria-label="$t('Prev')"
    >
      <SfIconChevronLeft /> {{ $t('Prev') }}
    </button>
    <ul class="justify-center hidden md:flex items-center gap-1">
      <li v-for="page in pages" :key="page">
        <div :class="['flex border-transparent']">
          <button
            type="button"
            :class="{ 'text-primary-500 bg-primary-100': page == currentPage }"
            class="btn md:w-12 rounded-md text-neutral-500 hover:bg-primary-100 hover:text-primary-800 active:bg-primary-200 active:text-primary-900"
            :aria-current="page === currentPage ? 'page' : undefined"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>
        </div>
      </li>
    </ul>
    <button
      type="button"
      class="px-4 py-3 btn flex whitespace-nowrap rounded-md text-neutral-500 hover:bg-primary-100 hover:text-primary-800 active:bg-primary-200 active:text-primary-900"
      @click="goToPage(currentPage + 1)"
      :disabled="isLastPage"
      :aria-label="$t('Next')"
    >
      {{ $t('Next') }} <SfIconChevronRight />
    </button>
  </nav>
</template>
